import { useState } from 'react'
import {
  Alert,
  Anchor,
  Box,
  Button,
  Card,
  Center,
  Divider,
  Group,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { IconAlertCircle, IconLock } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { ApiError } from '../api/client'
import { fetchSignInMethods, signIn } from '../api/session'
import { forgetEverything } from '../query-persistence'
import { sessionQueryOptions } from '../session-store'

/** Google's own mark, inline so the button needs no network to draw. */
function GoogleMark() {
  return (
    <svg width={16} height={16} viewBox="0 0 48 48" aria-hidden focusable="false">
      <path fill="#4285F4" d="M45.1 24.5c0-1.6-.1-3.1-.4-4.5H24v8.5h11.8a10 10 0 0 1-4.4 6.6v5.5h7.1c4.2-3.8 6.6-9.5 6.6-16.1z" />
      <path fill="#34A853" d="M24 46c6 0 11-2 14.5-5.4l-7.1-5.5a13 13 0 0 1-19.4-6.8H4.7v5.7A22 22 0 0 0 24 46z" />
      <path fill="#FBBC05" d="M12 28.3a13 13 0 0 1 0-8.6v-5.7H4.7a22 22 0 0 0 0 20l7.3-5.7z" />
      <path fill="#EA4335" d="M24 10.5c3.3 0 6.2 1.1 8.5 3.3l6.3-6.3A22 22 0 0 0 4.7 14l7.3 5.7A13 13 0 0 1 24 10.5z" />
    </svg>
  )
}

/**
 * Why a Google sign-in was turned away, in words.
 *
 * Each of these needs a different action from the person reading it, which is
 * the whole reason the server sends a reason rather than "sign-in failed".
 */
function googleMessage(reason: string, domains: string): string {
  switch (reason) {
    case 'domain_not_allowed':
      return `Only ${domains} accounts can sign in with Google. Use your work account.`
    case 'no_account':
      return 'That Google account is not set up in Medulla yet. Ask an administrator to add you.'
    case 'account_not_active':
      return 'That account is suspended. Ask an administrator to restore it.'
    case 'google_unavailable':
      return 'Signing in with Google is not available on this installation.'
    default:
      return 'Signing in with Google did not work. Try again, or use your password.'
  }
}

export function LoginPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const search = useSearch({ from: '/login' })
  const [formError, setFormError] = useState<string | null>(null)

  /*
    Which ways in exist. Asked rather than assumed, so an installation with
    no Google credentials configured simply does not offer the button - an
    option that fails when pressed is worse than one that is not there.
  */
  const methods = useQuery({
    queryKey: ['sign-in-methods'] as const,
    queryFn: ({ signal }) => fetchSignInMethods(signal),
    staleTime: 5 * 60_000,
  })

  const googleError = search.google_error
    ? googleMessage(search.google_error, methods.data?.google_domains ?? 'the company')
    : null

  const form = useForm({
    initialValues: { email: '', password: '' },
    validate: {
      email: (value) => (/^\S+@\S+\.\S+$/.test(value) ? null : 'Enter a valid email address'),
      password: (value) => (value.length > 0 ? null : 'Enter your password'),
    },
  })

  const login = useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) => signIn(email, password),
    onSuccess: async (user) => {
      // Whoever was here before, their cached answers are not this person's.
      forgetEverything(queryClient)
      // Seed the cache with what the login response already returned, so the
      // dashboard renders without a second round trip for the session.
      if (user) queryClient.setQueryData(sessionQueryOptions.queryKey, user)
      // The redirect comes from the URL, so it is treated as an untyped
      // destination rather than a known route.
      await navigate({ to: (search.redirect ?? '/') as '/' })
    },
    onError: (error) => {
      if (error instanceof ApiError && error.status === 429) {
        setFormError('Too many sign-in attempts. Wait a few minutes and try again.')
        return
      }

      if (error instanceof ApiError && error.isUnauthorized) {
        // Deliberately the same message whether the account exists or the
        // password is wrong - the API does not distinguish them either, so the
        // form cannot be used to discover which addresses are registered.
        setFormError('Those credentials are not valid.')
        return
      }

      setFormError('Could not reach Medulla. Check your connection and try again.')
    },
  })

  return (
    <Center mih="100dvh" p="md" bg="var(--mantine-color-body)">
      <Box w="100%" maw={400}>
        <Stack gap="lg">
          <Stack gap={4} align="center">
            <Title order={1}>Medulla</Title>
            <Text c="dimmed" size="sm">
              Sign in to continue
            </Text>
          </Stack>

          <Card padding="lg" radius="md">
            <form
              onSubmit={form.onSubmit((values) => {
                setFormError(null)
                login.mutate(values)
              })}
              noValidate
            >
              <Stack gap="md">
                {googleError && (
                  <Alert
                    icon={<IconAlertCircle size={16} />}
                    color="red"
                    variant="light"
                    role="alert"
                  >
                    {googleError}
                  </Alert>
                )}

                {formError && (
                  <Alert
                    icon={<IconAlertCircle size={16} />}
                    color="red"
                    variant="light"
                    role="alert"
                  >
                    {formError}
                  </Alert>
                )}

                <TextInput
                  label="Email"
                  placeholder="you@company.com"
                  type="email"
                  autoComplete="username"
                  autoFocus
                  required
                  {...form.getInputProps('email')}
                />

                <PasswordInput
                  label="Password"
                  placeholder="Your password"
                  autoComplete="current-password"
                  required
                  {...form.getInputProps('password')}
                />

                <Button
                  type="submit"
                  fullWidth
                  mt="xs"
                  leftSection={<IconLock size={16} />}
                  loading={login.isPending}
                >
                  Sign in
                </Button>
              </Stack>
            </form>

            {methods.data?.google && (
              <>
                <Divider label="or" labelPosition="center" my="md" />

                {/*
                  A plain link, not a fetch: signing in with Google is a
                  browser navigation to Google's own domain, which is the
                  only place it is safe for anybody to type a Google
                  password. An XHR could not take them there at all.
                */}
                <Button
                  component="a"
                  href="/api/v1/session/google"
                  fullWidth
                  variant="default"
                  leftSection={<GoogleMark />}
                >
                  Continue with Google
                </Button>
              </>
            )}
          </Card>

          <Group justify="center" gap={4}>
            <Text size="xs" c="dimmed">
              Locked out?
            </Text>
            <Anchor size="xs" href="mailto:support@oromedialab.com">
              Ask an administrator
            </Anchor>
          </Group>
        </Stack>
      </Box>
    </Center>
  )
}
